import React, { useState, useEffect } from 'react';
import { 
  Home, MapPin, Waves, Trees, Wifi, Tv, Coffee, Flame, 
  ParkingCircle, Wind, Shirt, TreePine, Utensils, Bed, Bath, 
  Footprints, Star, Calendar, Users, BedDouble, ExternalLink 
} from 'lucide-react';
import { propertyData } from '../data/mock';
import '../styles/maine-coastal.css';

const iconMap = {
  Home, MapPin, Waves, Trees, Wifi, Tv, Coffee, Flame,
  ParkingCircle, Wind, Shirt, TreePine, Utensils, Bed, Bath, Footprints
};

const PropertyHighlights = () => {
  return (
    <section className="section-spacing" style={{ background: 'var(--color-coastal-fog)' }}>
      <div className="container-coastal">
        <h2 className="section-title text-center">Why Choose Schoodic Serenity</h2>
        <p className="section-subtitle text-center" style={{ maxWidth: '700px', margin: '0 auto var(--spacing-xl) auto' }}>
          {propertyData.description}
        </p>
        
        <div className="grid-4">
          {propertyData.highlights.map((highlight, index) => {
            const IconComponent = iconMap[highlight.icon];
            return (
              <div key={index} className="card-coastal text-center">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  marginBottom: 'var(--spacing-md)' 
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'var(--color-ocean-deep)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <IconComponent size={28} color="white" />
                  </div>
                </div>
                <h3 className="card-title mb-sm">{highlight.title}</h3>
                <p className="body-text">{highlight.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const PropertyDetails = () => {
  const { propertyDetails } = propertyData;
  
  return (
    <section className="section-spacing">
      <div className="container-coastal">
        <h2 className="section-title text-center mb-lg">Property Details</h2>
        
        {/* Rating Badge */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: 'var(--spacing-xl)' 
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'var(--color-sunset-gold)',
            borderRadius: 'var(--border-radius-lg)',
            boxShadow: '0 4px 15px rgba(212, 165, 116, 0.3)'
          }}>
            <Star size={24} fill="white" color="white" />
            <span style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700',
              color: 'white'
            }}>
              {propertyDetails.rating}
            </span>
            <span style={{ 
              fontSize: '1rem',
              color: 'white',
              opacity: 0.95
            }}>
              · {propertyDetails.reviewCount} reviews
            </span>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'center', 
          gap: 'var(--spacing-xl)',
          marginBottom: 'var(--spacing-2xl)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <Users size={40} color="var(--color-ocean-deep)" />
            <div className="card-title mt-sm">{propertyDetails.guests}</div>
            <div className="body-text">Guests</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <BedDouble size={40} color="var(--color-ocean-deep)" />
            <div className="card-title mt-sm">{propertyDetails.bedrooms}</div>
            <div className="body-text">Bedrooms</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Bed size={40} color="var(--color-ocean-deep)" />
            <div className="card-title mt-sm">{propertyDetails.beds}</div>
            <div className="body-text">Beds</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Bath size={40} color="var(--color-ocean-deep)" />
            <div className="card-title mt-sm">{propertyDetails.bathrooms}</div>
            <div className="body-text">Bathrooms</div>
          </div>
        </div>

        <div className="card-coastal" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h3 className="card-title mb-md">Amenities</h3>
          <div className="grid-3">
            {propertyData.amenities.map((amenity, index) => {
              const IconComponent = iconMap[amenity.icon];
              return (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <IconComponent size={20} color="var(--color-ocean-deep)" />
                  <span className="body-text">{amenity.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const PhotoGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="section-spacing" style={{ background: 'var(--color-coastal-fog)' }}>
      <div className="container-coastal">
        <h2 className="section-title text-center mb-lg">Photo Gallery</h2>
        
        <div className="grid-3">
          {propertyData.photos.map((photo) => (
            <div 
              key={photo.id} 
              className="image-overlay-container"
              style={{ 
                height: '300px',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedImage(photo)}
            >
              <img src={photo.url} alt={photo.alt} />
              <div className="image-overlay" />
            </div>
          ))}
        </div>

        {selectedImage && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--spacing-lg)',
              cursor: 'pointer'
            }}
            onClick={() => setSelectedImage(null)}
          >
            <img 
              src={selectedImage.url} 
              alt={selectedImage.alt}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: 'var(--border-radius-lg)'
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

const Reviews = () => {
  return (
    <section className="section-spacing">
      <div className="container-coastal">
        <h2 className="section-title text-center mb-lg">Guest Reviews</h2>
        
        <div className="grid-2" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {propertyData.reviews.map((review) => (
            <div key={review.id} className="card-coastal">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 'var(--spacing-md)'
              }}>
                <div>
                  <h4 className="card-title" style={{ fontSize: '1.125rem', marginBottom: '0.25rem' }}>
                    {review.guest}
                  </h4>
                  <p className="small-text">{review.location}</p>
                </div>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--color-sunset-gold)" color="var(--color-sunset-gold)" />
                  ))}
                </div>
              </div>
              
              <p className="body-text mb-sm">"{review.text}"</p>
              
              <div style={{
                marginTop: 'var(--spacing-md)',
                padding: 'var(--spacing-sm)',
                background: 'var(--color-coastal-fog)',
                borderRadius: 'var(--border-radius)',
                borderLeft: '3px solid var(--color-ocean-deep)'
              }}>
                <p className="small-text" style={{ fontWeight: '600', color: 'var(--color-ocean-deep)' }}>
                  {review.highlight}
                </p>
              </div>
              
              <p className="small-text mt-sm">{review.date}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BookingCTA = () => {
  return (
    <section 
      className="section-spacing-large"
      style={{
        background: 'linear-gradient(135deg, var(--color-ocean-deep) 0%, var(--color-ocean-blue) 100%)',
        color: 'white'
      }}
    >
      <div className="container-coastal text-center">
        <h2 className="hero-title mb-md" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
          Ready for Your Maine Adventure?
        </h2>
        <p className="section-subtitle" style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '700px', margin: '0 auto var(--spacing-xl) auto' }}>
          Book your stay at Schoodic Serenity and experience the magic of coastal Maine. 
          Your unforgettable vacation awaits.
        </p>
        
        <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
          <a 
            href={propertyData.airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ 
              background: 'white', 
              color: 'var(--color-ocean-deep)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Book on Airbnb <ExternalLink size={18} />
          </a>
          <a 
            href="#availability"
            className="btn-outline"
            style={{ 
              borderColor: 'white',
              color: 'white'
            }}
          >
            <Calendar size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Check Availability
          </a>
        </div>
      </div>
    </section>
  );
};

export { PropertyHighlights, PropertyDetails, PhotoGallery, Reviews, BookingCTA };
